/**
 * PlayerSkills Page - Player-specific Skills Assessment and Tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingState from '../components/LoadingState';
import apiService from '../services/api';
import {
  Dribbble, Check, AlertTriangle, Eye, ArrowLeft, User, FileText, Clock, Activity, Lock
} from 'lucide-react';

// Sample skills categories for soccer
const skillCategories = [
  {
    name: 'Ball Control',
    skills: [
      { id: 'first_touch', name: 'First Touch', description: 'Controlling the ball on first contact' },
      { id: 'dribbling', name: 'Dribbling', description: 'Moving with the ball while maintaining control' },
      { id: 'juggling', name: 'Juggling', description: 'Keeping the ball airborne using feet, thighs, head' }
    ]
  },
  {
    name: 'Passing',
    skills: [
      { id: 'short_passing', name: 'Short Passing', description: 'Accurate passes over short distances' },
      { id: 'long_passing', name: 'Long Passing', description: 'Accurate passes over long distances' },
      { id: 'crossing', name: 'Crossing', description: 'Delivering the ball from wide areas' }
    ]
  },
  {
    name: 'Shooting',
    skills: [
      { id: 'finishing', name: 'Finishing', description: 'Scoring goals from various positions' },
      { id: 'volleys', name: 'Volleys', description: 'Striking the ball while in the air' },
      { id: 'free_kicks', name: 'Free Kicks', description: 'Taking direct and indirect free kicks' }
    ]
  },
  {
    name: 'Defending',
    skills: [
      { id: 'tackling', name: 'Tackling', description: 'Dispossessing opponents safely and effectively' },
      { id: 'positioning', name: 'Positioning', description: 'Being in the right place defensively' },
      { id: 'heading', name: 'Heading', description: 'Using the head to defend and clear the ball' }
    ]
  }
];

const PlayerSkills = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [player, setPlayer] = useState(null);
  const [skillsData, setSkillsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', 'error'

  // Check if current user is a coach (can edit) or parent (view only)
  const isCoach = user?.primary_role === 'coach' || user?.primary_role === 'admin';
  const isParent = user?.primary_role === 'parent';

  const loadPlayerAndSkills = useCallback(async () => {
    console.log(`==== LOADING PLAYER ${playerId} AND SKILLS ====`);
    console.log('Current auth status:', isAuthenticated);
    console.log('Current user:', user);

    try {
      setLoading(true);

      // Load family info to get player data
      const familyData = await apiService.getFamilyInfo();
      console.log('Family data received:', familyData);

      // Find the specific player
      const currentPlayer = familyData.players.find(p => p.id === parseInt(playerId));
      if (!currentPlayer) {
        console.error('Player not found');
        navigate('/profile');
        return;
      }

      setPlayer({
        id: currentPlayer.id,
        first_name: currentPlayer.first_name,
        last_name: currentPlayer.last_name,
        age: currentPlayer.date_of_birth ?
          new Date().getFullYear() - new Date(currentPlayer.date_of_birth).getFullYear() :
          'N/A',
        gender: currentPlayer.gender
      });

      // Load skills for this player
      try {
        console.log(`Making API call for player skills: /api/skills/player/${playerId}`);
        const playerSkillsResponse = await apiService.request(`/api/skills/player/${playerId}`);
        console.log('Player skills API response:', playerSkillsResponse);

        // Transform API response to internal format
        const apiSkillsData = {};
        if (playerSkillsResponse.player_skills && playerSkillsResponse.player_skills.length > 0) {
          playerSkillsResponse.player_skills.forEach(playerSkill => {
            // Map database skill IDs to our internal IDs
            const skillIdMappings = {
              1: ['first_touch'],
              2: ['dribbling'],
              3: ['juggling'],
              4: ['short_passing'],
              5: ['long_passing'],
              6: ['crossing'],
              7: ['finishing'],
              8: ['volleys'],
              9: ['free_kicks'],
              10: ['tackling', 'positioning', 'heading']
            };

            const skillIds = skillIdMappings[playerSkill.skill_id] || [`skill_${playerSkill.skill_id}`];

            skillIds.forEach(skillId => {
              apiSkillsData[skillId] = {
                level: playerSkill.current_level || 1,
                notes: playerSkill.notes || '',
                lastAssessed: playerSkill.last_assessed_at ?
                  new Date(playerSkill.last_assessed_at).toISOString().split('T')[0] :
                  new Date().toISOString().split('T')[0]
              };
            });
          });

          setSkillsData(apiSkillsData);
          console.log(`Loaded ${Object.keys(apiSkillsData).length} skills from API for player ${playerId}`);
        } else {
          console.log(`No skills found in API response for player ${playerId}, creating default data`);
          // Create default skills data
          const defaultSkills = {};
          skillCategories.forEach(category => {
            category.skills.forEach(skill => {
              defaultSkills[skill.id] = {
                level: 0,
                notes: '',
                lastAssessed: ''
              };
            });
          });
          setSkillsData(defaultSkills);
        }
      } catch (apiError) {
        console.log('Error loading skills from API, creating default data:', apiError.message);
        // Fallback: Create default skills data
        const defaultSkills = {};
        skillCategories.forEach(category => {
          category.skills.forEach(skill => {
            defaultSkills[skill.id] = {
              level: 0,
              notes: '',
              lastAssessed: ''
            };
          });
        });
        setSkillsData(defaultSkills);
      }
    } catch (error) {
      console.error('Error loading player and skills:', error);
      navigate('/profile');
    } finally {
      setLoading(false);
    }
  }, [playerId, isAuthenticated, user, navigate]);

  const updateSkillRating = (skillId, rating) => {
    const updatedSkillData = {
      ...skillsData[skillId],
      level: rating,
      lastAssessed: new Date().toISOString().split('T')[0]
    };

    setSkillsData(prev => ({
      ...prev,
      [skillId]: updatedSkillData
    }));
  };

  const updateSkillNotes = (skillId, notes) => {
    const updatedSkillData = {
      ...skillsData[skillId],
      notes: notes
    };

    setSkillsData(prev => ({
      ...prev,
      [skillId]: updatedSkillData
    }));
  };

  const saveSkills = async () => {
    try {
      setSaveStatus('saving');
      setLoading(true);

      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        throw new Error('Please login to save skills');
      }

      // Check if player is selected
      if (!player || !player.id) {
        throw new Error('No player selected');
      }

      // Map internal skill IDs to database skill IDs
      const skillIdMapping = {
        'first_touch': 1,
        'dribbling': 2,
        'juggling': 3,
        'short_passing': 4,
        'long_passing': 5,
        'crossing': 6,
        'finishing': 7,
        'volleys': 8,
        'free_kicks': 9,
        'tackling': 10,
        'positioning': 10,
        'heading': 10
      };

      // Transform skillsData to match the API format
      const skillsArray = Object.entries(skillsData)
        .filter(([skillId, skillData]) => skillData.level > 0)
        .map(([skillId, skillData]) => ({
          skill_id: skillIdMapping[skillId] || 1,
          current_level: skillData.level,
          notes: skillData.notes || ''
        }));

      if (skillsArray.length === 0) {
        throw new Error('Please rate at least one skill before saving');
      }

      console.log(`Saving skills for player ${player.id}:`, skillsArray);

      const result = await apiService.request(`/api/skills/player/${player.id}`, {
        method: 'POST',
        body: JSON.stringify({
          skills: skillsArray
        })
      });

      console.log('Skills saved successfully:', result);

      // Update the last assessed date for all skills
      const updatedSkillsData = {};
      Object.keys(skillsData).forEach(skillId => {
        updatedSkillsData[skillId] = {
          ...skillsData[skillId],
          lastAssessed: new Date().toISOString().split('T')[0]
        };
      });

      setSkillsData(updatedSkillsData);
      setSaveStatus('saved');

      setTimeout(() => setSaveStatus(''), 3000);

    } catch (error) {
      console.error('Error saving skills:', error);

      let errorMessage = 'Failed to save skills';
      if (error.message.includes('Player not found')) {
        errorMessage = 'Player not found or not authorized';
      } else if (error.message.includes('Not authenticated')) {
        errorMessage = 'Please login to save skills';
      } else if (error.message.includes('No player selected')) {
        errorMessage = 'No player selected';
      } else if (error.message.includes('Please rate at least one skill')) {
        errorMessage = 'Please rate at least one skill before saving';
      } else if (error.message.includes('Please login')) {
        errorMessage = 'Please login to save skills';
      }

      setSaveStatus(`error: ${errorMessage}`);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Skill rating component
  const SkillRating = ({ skillId, currentRating, canEdit = true }) => {
    return (
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <span style={{
          minWidth: '60px',
          fontSize: '0.9rem',
          color: 'var(--color-text-secondary)',
          fontWeight: 600
        }}>
          Level:
        </span>
        {[1, 2, 3, 4, 5].map(rating => (
          <button
            key={rating}
            onClick={canEdit ? () => updateSkillRating(skillId, rating) : undefined}
            disabled={!canEdit}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '2px solid var(--color-primary)',
              fontWeight: 700,
              fontSize: '1rem',
              transition: 'all 0.2s ease',
              background: currentRating >= rating ? 'var(--color-primary)' : 'transparent',
              color: currentRating >= rating ? 'var(--primary-black)' : 'var(--color-primary)',
              cursor: canEdit ? 'pointer' : 'default',
              opacity: canEdit ? 1 : 0.7
            }}
          >
            {rating}
          </button>
        ))}
        <span style={{
          marginLeft: '1rem',
          fontSize: '1.1rem',
          fontWeight: 700,
          color: 'var(--color-primary)',
          minWidth: '50px',
          textAlign: 'center'
        }}>
          {currentRating}/5
        </span>
        {!canEdit && currentRating > 0 && (
          <span style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-tertiary)',
            fontStyle: 'italic',
            marginLeft: '0.5rem'
          }}>
            (View Only)
          </span>
        )}
      </div>
    );
  };

  // Add useEffect to handle auth loading and redirection
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Load player and skills data when auth is ready
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      loadPlayerAndSkills();
    }
  }, [authLoading, isAuthenticated, user, playerId, loadPlayerAndSkills]);

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <PageLayout>
        <LoadingState />
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <PageLayout>
        <PageHeader
          title="Skills Assessment"
          subtitle="Track and develop player skills"
        />
        <Card 
          level={1} 
          style={{
            textAlign: 'center',
            maxWidth: '600px',
            margin: '2rem auto',
            padding: '3rem'
          }}
        >
          <div style={{
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Lock size={64} className="text-primary-green" />
          </div>
          <h3 style={{
            color: 'var(--color-primary)',
            marginBottom: '1rem',
            fontSize: '1.5rem',
            fontWeight: 700
          }}>
            Sign In Required
          </h3>
          <p style={{
            color: 'var(--color-text)',
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: 1.6
          }}>
            Please sign in to access player skills assessment and tracking.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/auth')}
          >
            Sign In
          </Button>
        </Card>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout>
        <PageHeader
          title="Skills Assessment"
          subtitle="Track and develop player skills"
        />
        <Card 
          level={1} 
          style={{
            textAlign: 'center',
            maxWidth: '600px',
            margin: '2rem auto',
            padding: '3rem'
          }}
        >
          <div style={{
            fontSize: '3rem',
            marginBottom: '1rem',
            animation: 'spin 2s linear infinite'
          }}>
            <Dribbble size={48} />
          </div>
          <div style={{
            color: 'var(--color-primary)',
            fontSize: '1.2rem',
            fontWeight: 600
          }}>
            Loading skills data...
          </div>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title={isCoach ? "Skills Assessment" : "Skills Progress"}
        subtitle={isCoach ? "Assess and track player soccer skills" : "View player's soccer skill progress"}
      />

      {/* Back to Profile Button */}
      <div style={{
        marginBottom: '1rem',
        maxWidth: '900px',
        margin: '0 auto 1rem auto'
      }}>
        <Button
          variant="outline"
          onClick={() => navigate('/profile')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <ArrowLeft size={16} />
          Back to Profile
        </Button>
      </div>

      {/* Player Information */}
      {player && (
        <Card 
          level={1} 
          style={{
            marginBottom: '2rem',
            maxWidth: '900px',
            margin: '0 auto 2rem auto',
            padding: '2rem'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            color: 'var(--color-primary)',
            marginBottom: '1rem',
            fontSize: '1.5rem',
            fontWeight: 700,
            gap: '0.75rem'
          }}>
            <User size={20} />
            Skills Assessment for {player.first_name} {player.last_name}
          </div>
          <div style={{
            fontSize: '1rem',
            color: 'var(--color-text-secondary)',
            display: 'flex',
            gap: '2rem'
          }}>
            <span>Age: {player.age}</span>
            {player.gender && <span>Gender: {player.gender.charAt(0).toUpperCase() + player.gender.slice(1)}</span>}
          </div>
        </Card>
      )}

      {/* Skills Assessment */}
      {player && (
        <div style={{
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          {skillCategories.map((category, categoryIndex) => (
            <Card 
              key={category.name} 
              variant="transparent" 
              style={{
                marginBottom: '2.5rem',
                padding: '2.5rem'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '2rem',
                paddingBottom: '1rem',
                borderBottom: '3px solid var(--color-primary)'
              }}>
                <div style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--primary-black)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  marginRight: '1rem'
                }}>
                  {categoryIndex + 1}
                </div>
                <h3 style={{
                  color: 'var(--color-primary)',
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  margin: 0
                }}>
                  {category.name}
                </h3>
              </div>

              <div style={{
                display: 'grid',
                gap: '2rem'
              }}>
                {category.skills.map((skill) => {
                  const skillData = skillsData[skill.id] || { level: 0, notes: '', lastAssessed: '' };

                  return (
                    <div 
                      key={skill.id} 
                      style={{
                        padding: '2rem',
                        background: 'var(--template-dark)',
                        borderRadius: '12px',
                        border: '2px solid var(--color-success)'
                      }}
                    >
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{
                          color: 'var(--color-primary)',
                          fontSize: '1.3rem',
                          fontWeight: 700,
                          marginBottom: '0.5rem'
                        }}>
                          {skill.name}
                        </h4>
                        <p style={{
                          color: 'var(--color-text-secondary)',
                          fontSize: '1rem',
                          margin: 0
                        }}>
                          {skill.description}
                        </p>
                      </div>

                      <SkillRating
                        skillId={skill.id}
                        currentRating={skillData.level}
                        canEdit={isCoach}
                      />

                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '1rem',
                          color: 'var(--color-primary)',
                          fontSize: '1.1rem',
                          fontWeight: 600
                        }}>
                          <FileText size={16} />
                          Notes
                        </div>
                        <textarea
                          value={skillData.notes}
                          onChange={isCoach ? (e) => updateSkillNotes(skill.id, e.target.value) : undefined}
                          placeholder={isCoach ? "Add notes about this skill..." : "No notes available"}
                          readOnly={!isCoach}
                          disabled={!isCoach}
                          style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '1rem',
                            background: 'var(--primary-black)',
                            border: '2px solid var(--color-success)',
                            borderRadius: 'var(--border-radius-md, 8px)',
                            color: 'var(--color-text)',
                            fontSize: '0.95rem',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            lineHeight: 1.5,
                            transition: 'border-color 0.2s ease',
                            opacity: isCoach ? 1 : 0.7,
                            cursor: isCoach ? 'text' : 'default'
                          }}
                        />
                      </div>

                      {skillData.lastAssessed && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: 'var(--color-text-tertiary)',
                          fontSize: '0.9rem',
                          fontStyle: 'italic'
                        }}>
                          <Clock size={14} />
                          Last assessed: {new Date(skillData.lastAssessed).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}

          {/* Save Button - Only show for coaches */}
          {isCoach && (
            <div style={{
              textAlign: 'center',
              marginBottom: '3rem'
            }}>
              <Button
                variant="primary"
                onClick={saveSkills}
                disabled={loading}
                style={{
                  minWidth: '250px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  margin: '0 auto',
                  opacity: saveStatus === 'saving' ? 0.7 : 1,
                  backgroundColor: saveStatus === 'saved' ? 'var(--color-primary)' : undefined,
                  color: saveStatus === 'saved' ? 'var(--primary-black)' : undefined
                }}
              >
                {saveStatus === 'saving' && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid currentColor',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {saveStatus === 'saved' && <Check size={16} />}
                {saveStatus.startsWith('error') && <AlertTriangle size={16} />}

                {saveStatus === 'saving' ? 'Saving Skills...' :
                  saveStatus === 'saved' ? 'Skills Saved!' :
                    saveStatus.startsWith('error') ? 'Save Failed' :
                      'Save Skills Assessment'}
              </Button>

              {saveStatus.startsWith('error') && (
                <div style={{
                  marginTop: '1rem',
                  color: 'var(--color-error)',
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}>
                  {saveStatus.split('error: ')[1]}
                </div>
              )}
            </div>
          )}

          {/* Info for Parents */}
          {isParent && (
            <div style={{ marginBottom: '3rem' }}>
              <Card 
                level={1} 
                style={{
                  textAlign: 'center',
                  padding: '2rem',
                  background: 'var(--color-success-light)',
                  border: '2px solid var(--color-success)'
                }}
              >
                <div style={{
                  marginBottom: '1rem',
                  fontSize: '2rem'
                }}>
                  <Eye size={24} />
                </div>
                <div style={{
                  color: 'var(--color-text)',
                  fontSize: '0.95rem',
                  lineHeight: 1.5
                }}>
                  Only coaches can assess and update player skills. As a parent, you can view your child's current skill assessments.
                </div>
              </Card>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default PlayerSkills;
